import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '../design-system';
import { Button } from '../design-system';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { businessCurrency } from '../../utils/businessDefaults';

export interface ProductVariant {
  id: string;
  name: string;
  sku?: string;
  price?: number;
  inventoryQuantity?: number;
  attributes: { [key: string]: string }; // e.g., { "size": "Large", "color": "Red" }
  images?: string[];
}

export interface ProductVariantFormProps {
  variants: ProductVariant[];
  onChange: (variants: ProductVariant[]) => void;
  basePrice?: number;
  currency?: string;
  containerStyle?: any;
}

export const ProductVariantForm: React.FC<ProductVariantFormProps> = ({
  variants = [],
  onChange,
  basePrice,
  currency = businessCurrency(),
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const [expandedVariant, setExpandedVariant] = useState<string | null>(null);

  const addVariant = () => {
    const newVariant: ProductVariant = {
      id: `variant-${Date.now()}`,
      name: '',
      attributes: {},
      price: basePrice,
    };
    onChange([...variants, newVariant]);
    setExpandedVariant(newVariant.id);
  };

  const removeVariant = (id: string) => {
    onChange(variants.filter((v) => v.id !== id));
    if (expandedVariant === id) {
      setExpandedVariant(null);
    }
  };

  const updateVariant = (id: string, updates: Partial<ProductVariant>) => {
    onChange(
      variants.map((v) => (v.id === id ? { ...v, ...updates } : v))
    );
  };

  const updateVariantAttribute = (
    variantId: string,
    attributeKey: string,
    value: string
  ) => {
    onChange(
      variants.map((v) => {
        if (v.id === variantId) {
          const newAttributes = { ...v.attributes };
          if (value) {
            newAttributes[attributeKey] = value;
          } else {
            delete newAttributes[attributeKey];
          }
          return { ...v, attributes: newAttributes };
        }
        return v;
      })
    );
  };

  const toggleExpand = (id: string) => {
    setExpandedVariant(expandedVariant === id ? null : id);
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.header}>
        <Text style={styles.title}>Product Variants</Text>
        <Button
          title="Add Variant"
          onPress={addVariant}
          variant="outline"
          size="sm"
          leftIcon={<Ionicons name="add" size={16} color={colors.primary} />}
        />
      </View>

      {variants.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name="layers-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyText}>No variants added</Text>
          <Text style={styles.emptySubtext}>
            Add variants to sell products in different sizes, colors, etc.
          </Text>
        </View>
      ) : (
        <ScrollView style={styles.variantsList}>
          {variants.map((variant, index) => (
            <View key={variant.id} style={styles.variantCard}>
              <TouchableOpacity
                style={styles.variantHeader}
                onPress={() => toggleExpand(variant.id)}
              >
                <View style={styles.variantHeaderLeft}>
                  <Text style={styles.variantNumber}>#{index + 1}</Text>
                  <Text style={styles.variantName} numberOfLines={1}>
                    {variant.name || 'Unnamed Variant'}
                  </Text>
                </View>
                <View style={styles.variantHeaderRight}>
                  {variant.price !== undefined && (
                    <Text style={styles.variantPrice}>
                      {currency} {variant.price.toFixed(2)}
                    </Text>
                  )}
                  <Ionicons
                    name={
                      expandedVariant === variant.id
                        ? 'chevron-up'
                        : 'chevron-down'
                    }
                    size={20}
                    color={colors.textLight}
                  />
                </View>
              </TouchableOpacity>

              {expandedVariant === variant.id && (
                <View style={styles.variantContent}>
                  <Input
                    label="Variant Name"
                    value={variant.name}
                    onChangeText={(text) =>
                      updateVariant(variant.id, { name: text })
                    }
                    placeholder="e.g., Large Red"
                  />

                  <Input
                    label="SKU (optional)"
                    value={variant.sku || ''}
                    onChangeText={(text) =>
                      updateVariant(variant.id, { sku: text })
                    }
                    placeholder="Variant SKU"
                  />

                  <Input
                    label={`Price (${currency})`}
                    value={
                      variant.price !== undefined
                        ? variant.price.toString()
                        : ''
                    }
                    onChangeText={(text) => {
                      const price = parseFloat(text);
                      updateVariant(variant.id, {
                        price: isNaN(price) ? undefined : price,
                      });
                    }}
                    placeholder={basePrice?.toString() || '0.00'}
                    keyboardType="numeric"
                  />

                  <Input
                    label="Inventory Quantity"
                    value={
                      variant.inventoryQuantity !== undefined
                        ? variant.inventoryQuantity.toString()
                        : ''
                    }
                    onChangeText={(text) => {
                      const qty = parseInt(text);
                      updateVariant(variant.id, {
                        inventoryQuantity: isNaN(qty) ? undefined : qty,
                      });
                    }}
                    placeholder="0"
                    keyboardType="numeric"
                  />

                  {/* Attributes */}
                  <View style={styles.attributesSection}>
                    <Text style={styles.attributesTitle}>Attributes</Text>
                    <Text style={styles.attributesSubtitle}>
                      e.g., Size: Large, Color: Red
                    </Text>

                    {/* Common attributes */}
                    {['size', 'color', 'material', 'style'].map((key) => (
                      <Input
                        key={key}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        value={variant.attributes[key] || ''}
                        onChangeText={(text) =>
                          updateVariantAttribute(variant.id, key, text)
                        }
                        placeholder={`Enter ${key}`}
                        containerStyle={styles.attributeInput}
                      />
                    ))}

                    {/* Custom attributes */}
                    {Object.keys(variant.attributes)
                      .filter((key) => !['size', 'color', 'material', 'style'].includes(key))
                      .map((key) => (
                        <View key={key} style={styles.customAttribute}>
                          <Input
                            label="Attribute Name"
                            value={key}
                            editable={false}
                            containerStyle={{ flex: 1 }}
                          />
                          <Input
                            label="Value"
                            value={variant.attributes[key]}
                            onChangeText={(text) =>
                              updateVariantAttribute(variant.id, key, text)
                            }
                            containerStyle={{ flex: 1 }}
                          />
                          <TouchableOpacity
                            style={styles.removeAttributeButton}
                            onPress={() =>
                              updateVariantAttribute(variant.id, key, '')
                            }
                          >
                            <Ionicons
                              name="close-circle"
                              size={24}
                              color={colors.error}
                            />
                          </TouchableOpacity>
                        </View>
                      ))}
                  </View>

                  <Button
                    title="Remove Variant"
                    onPress={() => removeVariant(variant.id)}
                    variant="ghost"
                    size="sm"
                    leftIcon={
                      <Ionicons name="trash-outline" size={16} color={colors.error} />
                    }
                  />
                </View>
              )}
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  emptyState: {
    alignItems: 'center',
    padding: spacing.xl,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginTop: spacing.xs,
    textAlign: 'center',
    fontFamily: typography.fontFamily.primary,
  },
  variantsList: {
    maxHeight: 500,
  },
  variantCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.sm,
    overflow: 'hidden',
  },
  variantHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
  },
  variantHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  variantNumber: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    marginRight: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  variantName: {
    fontSize: typography.fontSize.base,
    fontWeight: '500',
    color: colors.textDark,
    flex: 1,
    fontFamily: typography.fontFamily.medium,
  },
  variantHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  variantPrice: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  variantContent: {
    padding: spacing.md,
    paddingTop: 0,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  attributesSection: {
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  attributesTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  attributesSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.primary,
  },
  attributeInput: {
    marginBottom: spacing.sm,
  },
  customAttribute: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  removeAttributeButton: {
    padding: spacing.xs,
  },
});

